<template>
  <div class="ImageComponent"
    :class="classes"
    :style="styles"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave">

    <canvas class="ImageComponent-preview"
      ref="preview"
      v-if="smugmug"
      :width="preview.width"
      :height="preview.height"
      :style="previewStyles">
    </canvas>

    <img class="ImageComponent-image"
      v-if="resolvedSrc"
      ref="image"
      :class="imageClasses"
      :style="imageStyles"
      :data-src="resolvedSrc"
      :alt="resolvedAlt" />

    <img class="ImageComponent-image"
      v-if="resolvedSrcSet"
      ref="image"
      data-sizes="auto"
      :class="imageClasses"
      :style="imageStyles"
      :data-srcset="resolvedSrcSet"
      :alt="resolvedAlt" />

  </div>
</template>

<script>
import 'lazysizes/plugins/object-fit/ls.object-fit'
import 'lazysizes/plugins/parent-fit/ls.parent-fit'
import 'lazysizes/plugins/respimg/ls.respimg'
import 'lazysizes'

import config from 'src/config/smugmug.yaml'
import { assign } from 'lodash'
import StackBlur from 'stackblur-canvas'

const BLUR_RADIUS = 10

export default {
  name: 'ImageComponent',

  props: {
    /**
     * Whether to allow the image to start loading
     * - Used in GalleryGrid to prevent images from loading when the viewer is
     *   in the process of being opened
     */
    load: {
      type: Boolean,
      default: true
    },

    /**
     * Standard image source
     */
    src: {
      type: String
    },

    /**
     * SmugMug image ID
     */
    smugmug: {
      type: String
    },

    /**
     * Object-position property
     */
    position: {
      type: String,
      default: 'center'
    },

    /**
     * Alt text
     */
    alt: {
      type: String
    },

    /**
     * Whether to use zoom effect on hover
     * - If a number is given, how much to zoom
     */
    zoom: {
      type: [Boolean, Number],
      default: false
    },

    /**
     * Object-fit setting
     */
    fit: {
      type: String,
      default: 'cover'
    }
  },

  data () {
    return {
      /**
       * Whether image data has been loaded
       * @type {Boolean}
       */
      dataLoaded: false,

      /**
       * Whether we're ready to load the preview image
       * @type {Boolean}
       */
      readyToLoadPreview: false,

      /**
       * Resolved src attribute
       * @return {String}
       */
      resolvedSrc: null,

      /**
       * Resolved srcset attribute
       * @return {String}
       */
      resolvedSrcSet: null,

      /**
       * SmugMug image data
       * @type {Object}
       */
      data: {
        ratio: []
      },

      /**
       * Preview dimensions
       * TODO: can we move this into the preview loader below?
       * - cvs.width, cvs.height = etc
       * @type {Object}
       */
      preview: {
        width: 0,
        height: 0
      },

      /**
       * Holds the current image scale multiplier
       * - Used for image zoom
       * @type {Number}
       */
      imageScale: 1
    }
  },

  computed: {
    /**
     * Ratio to use in calculating image layout
     * - Checks SM data ratio next
     * @return {Array}
     */
    resolvedRatio () {
      if (!this.data.ratio.length) return
      return this.data.ratio
    },

    /**
     * Ratio expressed as a decimal
     * @return {Number}
     */
    ratioDecimal () {
      if (!this.resolvedRatio) return
      return this.resolvedRatio[1] / this.resolvedRatio[0]
    },

    /**
     * Ratio expressed as a percentage
     * @return {Number}
     */
    ratioPercentage () {
      if (!this.resolvedRatio) return
      return this.ratioDecimal * 100
    },

    /**
     * Classes for the main element
     * @return {Object}
     */
    classes () {
      // console.log(this.ratioDecimal)
      return {}
    },

    /**
     * Styles for the main element
     * @return {Object}
     */
    styles () {
      // No styles if no ratio exists
      if (!this.resolvedRatio) return {}

      return {
        'padding-top': `${this.ratioPercentage}%`
      }
    },

    /**
     * Classes for the image element
     * @return {Object}
     */
    imageClasses () {
      // Wait to add lazyload class until src is resolved
      return {
        'lazyload': this.resolvedSrc || this.resolvedSrcSet
      }
    },

    /**
     * Common image styles that the preview and image elements inherit from
     * @return {Object}
     */
    baseImageStyles () {
      let styles = {
        'transform': `scale(${this.imageScale})`
      }

      if (this.resolvedRatio) {
        styles = assign(styles, {
          'object-fit': this.fit,
          'object-position': this.position,
          'font-family': `object-fit: ${this.fit}, object-position: ${this.position}`
        })
      }

      return styles
    },

    /**
     * Styles for the preview element
     * @return {Object}
     */
    previewStyles () {
      return assign(this.baseImageStyles, {})
    },

    /**
     * Styles for the image element
     * @return {Object}
     */
    imageStyles () {
      let styles = assign(this.baseImageStyles, {})

      if (this.resolvedRatio) {
        styles = assign(styles, {
          position: 'absolute',
          top: 0,
          left: 0
        })
      }

      return styles
    },

    /**
     * Resolved alt attribute
     * - Uses provided alt text, falls back to SM image caption if present
     * @return {String}
     */
    resolvedAlt () {
      return this.alt || this.data.caption
    }
  },

  created () {
    // Wait to initialize until load is true
    if (!this.load) return
    this.init()
  },

  beforeDestroy () {
    this.removeEventListeners()
  },

  watch: {
    load () {
      if (!this.load) return
      this.init()
    },

    dataLoaded () {
      if (!this.dataLoaded) return
      this.$nextTick(this.addEventListeners)
    },

    readyToLoadPreview () {
      if (!this.readyToLoadPreview) return
      this.loadPreview()
    }
  },

  methods: {
    /**
     * Main initializer
     */
    init () {
      // Wait if neither 'src' or 'smugmug' are present
      if (!this.src && !this.smugmug) return

      return this.src ? this.initStandardImage() : this.initSmugmugImage()
    },

    /**
     * Initialize standard image
     */
    initStandardImage () {
      this.resolvedSrc = this.src
    },

    /**
     * Initialize SmugMug image
     */
    async initSmugmugImage () {
      // Fetch image data
      this.data = await this.$smugmug.get(this.smugmug)

      this.emitDataLoadedEvent()
      this.resolvedSrcSet = this.$smugmug.buildSrcset(this.data, config)
    },

    /**
     * Load preview image into the canvas element
     * - Uses the smallest image from config.image_sizes
     */
    async loadPreview () {
      const cvs = this.$refs.preview
      const ctx = cvs.getContext('2d')
      const img = new Image()
      const $el = this.$el

      const url = this.$smugmug.resolveTemplate(this.data, config.image_sizes[0])
      const proxiedUrl = config.proxy_url + url

      this.preview.width = $el.clientWidth
      this.preview.height = $el.clientWidth * this.ratioDecimal

      // TODO:
      // - Does this create a memory leak because of an abandoned listener?
      // - Set this.previewLoaded here somehow
      img.onload = function () {
        ctx.drawImage(img, 0, 0, cvs.width, cvs.height)
        StackBlur.canvasRGB(cvs, 0, 0, cvs.width, cvs.height, BLUR_RADIUS)
      }

      img.crossOrigin = 'https://benjandsara.com'
      img.src = proxiedUrl
    },

    /**
     * Emit data loaded event
     */
    emitDataLoadedEvent () {
      this.$emit('dataLoaded', this.data)
      this.dataLoaded = true
    },

    /**
     * Handle mouseenter event on main element
     */
    handleMouseEnter () {
      if (!this.zoom) return

      // Handle default zoom level
      if (this.zoom === true) {
        this.imageScale = 1.025
        return
      }

      // Handle custom zoom level
      this.imageScale = this.zoom
    },

    /**
     * Handle mouse leave event on the top-level element
     */
    handleMouseLeave () {
      if (!this.zoom) return
      this.imageScale = 1
    },

    /**
     * Add event listeners
     */
    addEventListeners () {
      this.$refs.image.addEventListener('lazybeforesizes', this.handleImageLazyBeforeSizes)
    },

    /**
     * Remove event listeners
     */
    removeEventListeners () {
      this.$refs.image.removeEventListener('lazybeforesizes', this.handleImageLazyBeforeSizes)
    },

    /**
     * Handle lazybeforesizes event on the image element
     */
    handleImageLazyBeforeSizes () {
      this.readyToLoadPreview = true
    }
  }
}
</script>

<style>
@import 'properties';

.ImageComponent {
  position: relative;
  overflow: hidden;
}

.ImageComponent-preview,
.ImageComponent-image {
  display: block;
  width: 100%;
  height: 100%;
  will-change: transform, opacity;
  transition: transform 0.5s ease, opacity 0.5s ease;
}

.ImageComponent-image {
  opacity: 0;
}

.ImageComponent-image.lazyloaded {
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// ImageComponent.vue?471b727b